import type { ComponentProps } from "react";
import type { PayrollDetailDTO } from "@/modules/hr/domain/contracts";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { PrintDocumentShell } from "@/shared/ui/PrintDocumentShell";

interface PayslipPdfPreviewContentProps {
  // PayrollDetailDTO currently provides only payroll summary fields.
  // This component needs employee fields + line items for the full payslip print layout,
  // so we accept them as optional extras.
  payroll: PayrollDetailDTO & {
    employeeName?: string;
    employeeEmail?: string;
    departmentName?: string | null;
    positionTitle?: string | null;
    items?: Array<{
      id: string;
      type: "earning" | "deduction";
      name: string;
      amount: number;
    }>;
  };
}

export function PayslipPdfPrintShell(
  props: Omit<ComponentProps<typeof PrintDocumentShell>, "rootId">,
) {
  return <PrintDocumentShell {...props} rootId="payslip-pdf-content" />;
}

const MONTH_NAMES = [
  "",
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ມ.ສ.",
  "ພ.ສ.",
  "ມ.ຖ.",
  "ກ.ກ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
];

const STATUS_LABEL: Record<string, string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນ",
  paid: "ຊຳລະແລ້ວ",
};

export function PayslipPdfPreviewContent({
  payroll,
}: PayslipPdfPreviewContentProps) {
  const items = payroll.items ?? [];

  const periodLabel = MONTH_NAMES[payroll.periodMonth]
    ? `${MONTH_NAMES[payroll.periodMonth]} ${payroll.periodYear}`
    : `${payroll.periodYear}/${payroll.periodMonth}`;

  return (
    <div
      id="payslip-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style (same as internal-audit) */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ໃບເງິນເດືອນ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ພະນັກງານ:{" "}
              <span className="font-normal">{payroll.employeeName ?? "-"}</span>
            </p>
            <p className="mt-1 font-semibold">
              ເດືອນ: <span className="font-normal">{periodLabel}</span>
            </p>
            <p className="mt-1 font-semibold">
              ເລກທີ:{" "}
              <span className="font-normal">
                {payroll.id.slice(-6).toUpperCase()}
              </span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* ຂໍ້ມູນພະນັກງານ */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          ຂໍ້ມູນພະນັກງານ
        </h3>
        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຊື່ພະນັກງານ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {payroll.employeeName ?? "-"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ອີເມວ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {payroll.employeeEmail ?? "-"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກ / ຕໍາແໜ່ງ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {payroll.departmentName ?? "-"} / {payroll.positionTitle ?? "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ລາຍການເງິນເດືອນ */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          ລາຍການເງິນເດືອນ
        </h3>
        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ເງິນເດືອນພື້ນຖານ
              </td>
              <td className="p-4 text-right tabular-nums">
                {payroll.baseSalary.toLocaleString("en-US")}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                OT / ໂບນັສ
              </td>
              <td className="p-4 text-right tabular-nums">
                {(payroll.totalEarnings - payroll.baseSalary).toLocaleString(
                  "en-US",
                )}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ລາຍໄດ້ລວມ
              </td>
              <td className="p-4 text-right tabular-nums">
                {payroll.totalEarnings.toLocaleString("en-US")}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຫັກລວມ
              </td>
              <td className="p-4 text-right tabular-nums">
                -{payroll.totalDeductions.toLocaleString("en-US")}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-bold text-gray-800">
                ຍອດສຸດທິ
              </td>
              <td className="p-4 text-right font-bold tabular-nums">
                {payroll.netSalary.toLocaleString("en-US")}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ລາຍການລະອຽດ (items) */}
      {items.length ? (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            ລາຍການລະອຽດ
          </h3>
          <table className="mb-6 w-full border-collapse border-2 border-gray-400">
            <thead>
              <tr className="bg-gray-100">
                <th className="border-gray-300 border-r border-b p-4 text-left font-semibold text-gray-700">
                  ລາຍການ
                </th>
                <th className="border-gray-300 border-b p-4 text-left font-semibold text-gray-700">
                  ປະເພດ
                </th>
                <th className="border-gray-300 border-b border-l p-4 text-right font-semibold text-gray-700">
                  ຈຳນວນ
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-gray-300 border-b">
                  <td className="border-gray-300 border-r p-4 text-sm">
                    {item.name}
                  </td>
                  <td className="p-4 text-sm">
                    {item.type === "earning" ? "ລາຍໄດ້" : "ຫັກ"}
                  </td>
                  <td className="border-gray-300 border-l p-4 text-right text-sm tabular-nums">
                    {item.type === "deduction" ? "-" : ""}
                    {item.amount.toLocaleString("en-US")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* ສະຖານະ */}
      <div className="mb-10">
        <p className="text-gray-700 text-sm">
          <span className="font-semibold">ສະຖານະ:</span>{" "}
          {STATUS_LABEL[payroll.status] ?? payroll.status}
          {payroll.paidAt ? ` | ວັນທີຈ່າຍ: ${payroll.paidAt}` : ""}
        </p>
      </div>

      {/* Footer (same as internal-audit) */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-sm">
        <p>ເອກະສານນີ້ຖືກສ້າງອັດຕະໂນມັດໂດຍລະບົບ Freedom ERP</p>
        <p className="mt-1">
          ວັນທີພິມ: {formatDateTimeLocal(new Date())} | ເລກທີ: {payroll.id}
        </p>
      </div>
    </div>
  );
}
